/**
 * Full-screen cluster view showing a group of contacts with a colored header.
 * Used when tapping into location, industry, company, or skill clusters.
 */
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import {
  getInitials,
  getFullName,
  getRoleDisplay,
  getAvatarColors,
  type Contact,
} from "@/lib/data";

function ClusterAvatar({ contact, size }: { contact: Contact; size: number }) {
  const [bg1, bg2] = getAvatarColors(contact);
  const fs = Math.round(size * 0.3);
  if (contact.photo) {
    return (
      <img
        src={contact.photo}
        alt=""
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full"
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${bg1}, ${bg2})` }}
    >
      <span className="font-medium text-white" style={{ fontSize: fs }}>
        {getInitials(contact)}
      </span>
    </div>
  );
}

interface ClusterViewProps {
  title: string;
  subtitle?: string;
  contacts: Contact[];
  accentColor: string;
  onSelect: (contact: Contact) => void;
  onBack: () => void;
}

export function ClusterView({
  title,
  subtitle,
  contacts,
  accentColor,
  onSelect,
  onBack,
}: ClusterViewProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleBack = () => {
    setMounted(false);
    setTimeout(onBack, 280);
  };

  const headerAvatars = contacts.slice(0, 6);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
      style={{
        backgroundColor: "oklch(0.13 0.02 250)",
        transform: mounted ? "translateY(0)" : "translateY(100%)",
      }}
    >
      <div
        className="relative shrink-0 overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${accentColor}35 0%, ${accentColor}15 50%, transparent 100%)` }}
      >
        <div className="h-[env(safe-area-inset-top,0px)]" />
        <div className="absolute -right-12 -top-12 h-56 w-56 rounded-full opacity-30 blur-3xl" style={{ backgroundColor: accentColor }} />
        <div className="absolute -left-8 top-12 h-40 w-40 rounded-full opacity-20 blur-2xl" style={{ backgroundColor: accentColor }} />
        <div className="relative px-5 pb-7 pt-3">
          <button
            onClick={handleBack}
            className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.08] backdrop-blur-sm transition-all active:scale-95"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div className="mb-5 flex -space-x-3">
            {headerAvatars.map((c) => (
              <div key={c.id} className="rounded-full border-[2.5px] border-background">
                <ClusterAvatar contact={c} size={48} />
              </div>
            ))}
            {contacts.length > 6 && (
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-[2.5px] border-background bg-white/[0.1]">
                <span className="text-[11px] font-medium text-white/50">+{contacts.length - 6}</span>
              </div>
            )}
          </div>
          <h1 className="text-[34px] font-extrabold tracking-tight" style={{ color: accentColor }}>
            {title}
          </h1>
          <p className="mt-1 text-[15px] text-white/40">
            {subtitle ?? `${contacts.length} contact${contacts.length !== 1 ? "s" : ""}`}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-32">
        {contacts.map((contact, i) => {
          const role = getRoleDisplay(contact);
          const city = contact.addresses[0]?.city ?? contact.currentCity;
          return (
            <button
              key={contact.id}
              onClick={() => onSelect(contact)}
              className="flex w-full items-center gap-4 py-3.5 text-left transition-all active:opacity-70"
            >
              <span className="w-6 text-right text-[14px] font-medium tabular-nums text-white/20">{i + 1}</span>
              <ClusterAvatar contact={contact} size={52} />
              <div className={`min-w-0 flex-1 ${i < contacts.length - 1 ? "border-b border-white/[0.05] pb-3.5" : ""}`}>
                <p className="text-[16px] font-semibold text-white">{getFullName(contact)}</p>
                {role && <p className="mt-0.5 truncate text-[13px] text-white/40">{role}</p>}
                {city && !role && <p className="mt-0.5 text-[13px] text-white/30">{city}</p>}
              </div>
              {contact.claimed && (
                <div className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: accentColor }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
